import React, { useState, useEffect, useId } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { Loader } from '@common/components';
import { useSyncAnomaliesQuery } from '@dashboard/api/syncMonitor';
import TagBadge, {
  CenterStatusBadge,
  HistoricSinceBadge,
} from '@dashboard/components/SyncMonitor/TagBadge/TagBadge';
import CopyId from '@dashboard/components/SyncMonitor/CopyId';
import { Pager } from '@dashboard/components/SyncMonitor/HolesPanel/HolesPanel';
import classes from '@dashboard/components/SyncMonitor/HolesPanel/HolesPanel.module.scss';

const CODES = [
  'historic_pf_fresh',
  'historic_fresh',
  'active_stale',
  'active_never',
  'new_stale',
  'disabled_fresh',
  'active_status_never',
];
const LIMIT = 50;

/**
 * Suitcases whose tags and center-status do not fit together (see "Reglas"). The tag filter of the
 * page narrows the list; the type and the text box narrow it further.
 */
export default function AnomaliesPanel({ tags }) {
  const { t } = useTranslation('dashboard\\SyncMonitorView', {
    useSuspense: false,
  });
  const selectId = useId();
  const [code, setCode] = useState('');
  const [text, setText] = useState('');
  const [q, setQ] = useState('');
  const [page, setPage] = useState(1);

  useEffect(() => {
    const timer = setTimeout(() => setQ(text.trim()), 300);
    return () => clearTimeout(timer);
  }, [text]);

  useEffect(() => {
    setPage(1);
  }, [code, q, tags.join(',')]);

  const {
    data: resp,
    isFetching,
    isError,
  } = useSyncAnomaliesQuery({
    code: code || undefined,
    tags,
    q: q || undefined,
    page,
    limit: LIMIT,
  });

  const data = resp?.data;
  const rows = data?.rows ?? [];
  const counts = data?.counts ?? {};
  const total = data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / LIMIT));

  const body = () => {
    if (isFetching)
      return (
        <div className={classes.loaderWrap}>
          <Loader />
        </div>
      );
    if (isError) return <p className={classes.empty}>{t('anomaliesLoadError')}</p>;
    if (rows.length === 0)
      return <p className={classes.empty}>{t('anomaliesEmpty')}</p>;

    return (
      <table className={classes.table}>
        <thead>
          <tr>
            <th>{t('holesColCenter')}</th>
            <th>{t('holesColFreshness')}</th>
            <th>{t('anomaliesColAnomalies')}</th>
            <th>{t('holesColLastData')}</th>
            <th>{t('anomaliesColDays')}</th>
          </tr>
        </thead>
        <tbody>
          {rows.map((r) => (
            <tr key={r.center_id}>
              <td>
                {r.center_name}
                <span className={classes.ext}>
                  {' · '}
                  <CopyId value={r.center_id} />
                </span>
              </td>
              <td>
                <CenterStatusBadge status={r.center_status} />
                {(r.tags || []).map((tag) => (
                  <TagBadge key={tag} tag={tag} />
                ))}
                <HistoricSinceBadge date={r.historic_since} />
                {r.suitcases_count > 1 && (
                  <span
                    className={`${classes.badge} ${classes.suitcaseBadge}`}
                    title={t('holesSuitcaseOf', { count: r.suitcases_count })}
                  >
                    {t('holesSuitcaseCount', { count: r.suitcases_count })}
                  </span>
                )}
              </td>
              <td>
                {(r.anomalies || []).map((c) => (
                  <span
                    key={c}
                    className={`${classes.badge} ${classes.kind_both}`}
                    title={t(`anomalyHint_${c}`, {
                      maxAgeDays: 365,
                      newQuietDays: 30,
                      newMaxDays: 90,
                    })}
                  >
                    {t(`anomaly_${c}`)}
                  </span>
                ))}
              </td>
              <td className={classes.mono}>{r.last_data_date ?? '—'}</td>
              <td className={classes.mono}>{r.days_with_data}</td>
            </tr>
          ))}
        </tbody>
      </table>
    );
  };

  return (
    <div className={classes.panel}>
      <div className={classes.header}>
        <h3 className={classes.title}>
          {t('tabAnomalies')}
          {data && (
            <span className={classes.count}>
              {t('anomaliesCount', { count: total })}
            </span>
          )}
        </h3>
        <div className={classes.headerActions}>
          <Pager page={page} totalPages={totalPages} onPage={setPage} />
        </div>
      </div>

      <p className={classes.hint}>{t('anomaliesIntro')}</p>

      <div className={classes.filters}>
        <div className={classes.field}>
          <label htmlFor={selectId} className={classes.fieldLabel}>
            {t('anomaliesFilterType')}
          </label>
          <select
            id={selectId}
            className={classes.select}
            value={code}
            onChange={(e) => setCode(e.target.value)}
          >
            <option value="">{t('anomaliesAll')}</option>
            {CODES.map((c) => (
              <option key={c} value={c}>
                {`${t(`anomaly_${c}`)} (${counts[c] ?? 0})`}
              </option>
            ))}
          </select>
        </div>
        <div className={classes.field}>
          <span className={classes.fieldLabel}>{t('filterSearch')}</span>
          <input
            type="text"
            className={classes.select}
            value={text}
            placeholder={t('searchInListPlaceholder')}
            onChange={(e) => setText(e.target.value)}
          />
        </div>
      </div>

      {body()}
    </div>
  );
}

AnomaliesPanel.propTypes = {
  tags: PropTypes.arrayOf(PropTypes.string),
};

AnomaliesPanel.defaultProps = {
  tags: [],
};
